import type { MeOutput } from '@garage/shared';
import { useOptionalActiveMe } from './me.js';

/**
 * Adresses de l'espace garage (vague 5) : `/g/<slug>/…`. Le slug sert à naviguer et ne donne aucun
 * droit (le serveur ne le lit jamais pour une route `garage`). Aucune donnée personnelle dans une
 * adresse : seulement le slug du garage et des identifiants opaques.
 */
export function garagePath(slug: string, sub = ''): string {
  const rest = sub.replace(/^\/+/, '');
  return `/g/${slug}/${rest}`;
}

/** Sous-chemins de l'espace garage. */
export const GP = {
  home: '',
  /** « À l'atelier » (ex-Parc, décision du 02/10/2026) : adresse gardée pour ne casser aucun lien. */
  parc: 'parc',
  /**
   * Ancienne adresse « Interventions » (ex-Dossiers) : redirige vers « Rendez-vous » (ou l'Agenda quand le
   * module est actif), décision du 03/10/2026 ; gardée pour les liens des e-mails, notifications et favoris.
   */
  dossiers: 'dossiers',
  /** « Rendez-vous » (03/10/2026) : arrivées prévues, groupées par jour. */
  rendezVous: 'rendez-vous',
  /** « Base garage » : annuaire des clients et des véhicules (`directory.search`). */
  base: 'base',
  entree: 'entree',
  messagerie: 'messagerie',
  /** Vague 7 : agenda (module facultatif). */
  agenda: 'agenda',
  /** Ancienne adresse « Chiffres » (02/10/2026) : redirige vers « Gestion » → Volume financier (03/10/2026). */
  chiffres: 'chiffres',
  /** 03/10/2026 : « Gestion » (propriétaire et associés) : historique, activité, volume financier. */
  gestion: 'gestion',
  gestionTab: (tab: string) => `gestion/${tab}`,
  conversation: (interventionId: string) => `messagerie/${interventionId}`,
  intervention: (interventionId: string) => `interventions/${interventionId}`,
  vehicle: (vehicleId: string) => `vehicules/${vehicleId}`,
  /** Fiche client (Base garage), adressée par son identifiant opaque. */
  customer: (customerId: string) => `clients/${customerId}`,
  compte: 'compte',
  compteSection: (section: string) => `compte/${section}`,
  supportAccess: (accessId: string) => `compte/support/${accessId}`,
} as const;

/** Construit une adresse du garage actif. Hors espace garage (tests de composants) : chemin relatif à la racine. */
export function useGaragePath(): (sub?: string) => string {
  const me = useOptionalActiveMe();
  const slug = me?.active.garageSlug;
  return (sub = '') => (slug ? garagePath(slug, sub) : `/${sub.replace(/^\/+/, '')}`);
}

/** Appartenances utilisables (garage actif, non suspendu). */
export function usableMemberships(me: MeOutput): MeOutput['memberships'] {
  return me.memberships.filter((m) => m.status === 'active');
}

/**
 * Où aller après la connexion (ou à l'adresse racine) :
 * - administrateur plateforme sans appartenance → `/admin` ;
 * - plusieurs garages, ou administrateur **et** membre → écran de choix (ses garages + console) ;
 * - un seul garage → `/g/<slug>/` ;
 * - aucun garage utilisable (aucun, ou tous suspendus) → écran de choix (message clair).
 * `afterLogin` : à la connexion, l'administrateur membre choisit toujours ; ensuite, le garage actif
 * de la session est repris.
 */
export function landingPath(me: MeOutput, opts: { afterLogin?: boolean } = {}): string {
  const usable = usableMemberships(me);
  if (me.isPlatformAdmin && me.memberships.length === 0) return '/admin';
  if (!opts.afterLogin && me.active) return garagePath(me.active.garageSlug);
  if (me.isPlatformAdmin) return '/choose-garage';
  if (usable.length === 1 && usable[0]) return garagePath(usable[0].garageSlug);
  return '/choose-garage';
}
